import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [state, setState] = useState("polling"); // polling | paid | processing | error
  const [booking, setBooking] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("processing");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center px-5 py-16">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-96 w-[40rem] rounded-full bg-sky-500/20 blur-3xl" />
      <Link
        to="/"
        className="relative z-10 mb-10 inline-flex items-center gap-2 font-display font-extrabold text-lg"
      >
        <img
          src="/images/logo.png"
          alt="Action Packed Adventures logo"
          className="h-9 w-9 rounded-full object-cover"
        />
        Action Packed <span className="text-sky-400">Adventures</span>
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="payment-success-card"
        className="relative z-10 w-full max-w-lg rounded-[2rem] bg-white/5 border border-white/10 p-8 md:p-10 text-center backdrop-blur-xl"
      >
        {state === "polling" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-sky-400 animate-spin" />
            <h1 className="mt-6 font-display font-black text-3xl">
              Confirming your payment…
            </h1>
            <p className="mt-3 text-slate-300 font-medium">
              Just a moment while we confirm everything with Square.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-sky-400" />
            <h1 className="mt-6 font-display font-black text-3xl md:text-4xl">
              Booking confirmed!
            </h1>
            <p className="mt-3 text-slate-300 font-medium">
              Thank you{booking?.parent_name ? `, ${booking.parent_name}` : ""} —
              your place{(booking?.children?.length || 0) > 1 ? "s are" : " is"}{" "}
              booked. Please keep this page for your records; we look forward to
              seeing you.
            </p>
            {booking?.children?.length > 0 && (
              <div className="mt-6 text-left rounded-2xl bg-slate-900/60 p-5 space-y-2">
                {booking.children.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-slate-400">
                      {c.days.length === 4
                        ? "Full 4-day camp"
                        : `${c.days.length} ${c.days.length === 1 ? "day" : "days"}`}
                    </span>
                  </div>
                ))}
                <div className="border-t border-white/10 pt-3 flex justify-between font-display font-bold">
                  <span>Total paid</span>
                  <span className="text-sky-400">
                    £{((booking.amount || 0) / 100).toFixed(0)}
                  </span>
                </div>
              </div>
            )}
            <p className="mt-6 text-sm text-slate-400">
              See you at The Downs Preparatory School!
            </p>
          </>
        )}

        {state === "processing" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-sky-400" />
            <h1 className="mt-6 font-display font-black text-3xl">
              Payment processing
            </h1>
            <p className="mt-3 text-slate-300 font-medium">
              Your payment is being processed. If you have been charged, your
              booking is secure — do get in touch if you have any questions.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <AlertCircle className="h-14 w-14 mx-auto text-yellow-400" />
            <h1 className="mt-6 font-display font-black text-3xl">
              We could not confirm this
            </h1>
            <p className="mt-3 text-slate-300 font-medium">
              Something went wrong confirming your payment. Please try again or
              get in touch and we will happily sort it out.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-8 inline-flex items-center justify-center rounded-full bg-sky-400 px-8 py-3.5 font-display font-bold text-slate-900 hover:bg-sky-300 transition-colors"
        >
          Back to home
        </Link>
      </motion.div>
    </div>
  );
}
